// English string catalogue. Mirrors he.ts so the runtime can switch the entire UI
// without changing component logic.

export const en = {
  app: {
    name: "Video Hearing",
    policeBrand: "Video Hearing - Israel Police",
    startupError: "The system failed to load. Please refresh or contact support."
  },
  nav: {
    home: "Home",
    schedule: "Meetings",
    admin: "Admin",
    switchLanguage: "Switch language",
    placeholder: "This screen will be built in the next development phases. See"
  },
  banner: {
    reconnecting: "Reconnecting...",
    recordingOn: "Recorded"
  },
  launch: {
    caseContext: "Case details",
    driver: "Driver ID",
    caseRef: "Reference",
    system: "Source system"
  },
  call: {
    precallTitle: "Select an officer for the hearing",
    search: "Search officer...",
    available: "Available",
    unavailable: "Unavailable",
    callAction: "Call",
    noOfficers: "No officers are available right now.",
    callingTitle: "Calling...",
    cancel: "Cancel",
    incomingTitle: "Incoming call",
    accept: "Answer",
    decline: "Decline",
    endCall: "End call",
    endedTitle: "The call has ended",
    backToList: "Back to list",
    mute: "Mute",
    unmute: "Unmute",
    camOff: "Turn camera off",
    camOn: "Turn camera on",
    selfPanel: "Field officer and citizen",
    officerPanel: "Traffic officer",
    availabilityToggle: "Availability status",
    shareScreen: "Share screen",
    stopShare: "Stop sharing",
    screenSharing: "Screen sharing",
    controlsLabel: "Call controls",
    fullscreen: "Full screen",
    exitFullscreen: "Exit full screen",
    officerStation: "Officer station",
    waitingTitle: "Waiting for an incoming call",
    waitingStation: "This station is ready and available to receive incoming calls.",
    localPreview: "Local preview",
    tokenExpired: "The connection expired. Please open the demo link again.",
    connectFailed: "Could not connect to the call service. Refresh the page or open the demo link again.",
    missingCallId: "No call identifier was found for starting the call.",
    hubNotReady: "The call service is not connected yet. Refresh the page or open the demo link again.",
    startFailed: "Could not start the call.",
    callerFallback: "Officer"
  },
  readiness: {
    title: "Camera and microphone check",
    subtitle: "Make sure the camera and microphone work before starting the call.",
    camera: "Camera",
    microphone: "Microphone",
    cameraOn: "Camera on",
    cameraOff: "Camera off",
    micOn: "Microphone on",
    micOff: "Microphone muted",
    cameraOffPreview: "Camera is off",
    continueTraffic: "Ready - start receiving calls",
    continueField: "Continue to officer selection",
    retry: "Try again",
    requesting: "Requesting camera and microphone access...",
    noDevice: "No available camera or microphone was found. Connect a device and try again."
  },
  availability: {
    available: "Available",
    unavailable: "Unavailable",
    title: "Availability status"
  },
  schedule: {
    title: "Scheduled meetings",
    none: "No scheduled meetings.",
    join: "Join",
    cancel: "Cancel",
    start: "Start",
    duration: "Duration (min)",
    caseRef: "Reference"
  },
  admin: {
    title: "Management Console",
    officers: "Officers",
    recordings: "Recordings",
    dashboard: "Dashboard",
    config: "Configuration",
    notAuthorized: "Administrator permission is required to view this console. Sign in with an administrator or IT viewer account.",
    sessionExpired: "The session expired. Reopen the management link with an administrator account.",
    redirecting: "Redirecting to administrator sign-in..."
  },
  dash: {
    subtitle: "Operational status of the hearing system",
    updated: "Updated",
    refresh: "Refresh",
    sectionStatus: "Operational status",
    sectionHealth: "Service health",
    sectionRecordings: "Recording status",
    sectionOfficers: "Officer availability",
    sectionActivity: "Recent activity",
    sectionMetrics: "Infrastructure and monitoring metrics",
    officersAvailable: "Available officers",
    officersUnavailable: "Unavailable",
    recInProgress: "Recordings in progress",
    recCompleted: "Completed recordings",
    recFailed: "Failed recordings",
    recQueued: "Queued",
    recSigned: "Digitally signed",
    storageUsed: "Storage used",
    totalMinutes: "Recording minutes",
    latestRecording: "Latest recording",
    of: "of",
    serviceUp: "Up",
    serviceDown: "Unavailable",
    metricsNotReported: "Infrastructure monitoring metrics have not reported yet. The operational data above reflects the current system state.",
    noActivity: "No recent recording activity.",
    secondsUnit: "sec",
    services: {
      "app-service": "Application service",
      "media-service": "Media service",
      "compositing-worker": "Recording processor"
    } as Record<string, string>,
    kpi: {
      vc_active_calls: "Active calls",
      vc_configured_capacity: "Configured capacity",
      vc_available_slots: "Available slots",
      vc_capacity_utilization_ratio: "Capacity utilization",
      vc_call_setup_seconds_p95: "Call setup time (p95)",
      vc_call_failure_ratio: "Call failure ratio",
      vc_reconnect_ratio: "Reconnect ratio",
      vc_compositing_queue_depth: "Recording processing queue",
      vc_compositing_latency_seconds_p95: "Processing time (p95)",
      vc_compositing_failed_total: "Processing failures",
      vc_recording_capture_faults_total: "Capture faults",
      vc_scratch_store_free_ratio: "Scratch storage free",
      vc_recording_store_free_ratio: "Recording storage free",
      vc_cpu_headroom_ratio: "CPU headroom"
    } as Record<string, string>
  },
  rec: {
    search: "Search (system / reference / ID / file name)",
    statusAll: "All statuses",
    statusCompleted: "Completed",
    statusQueued: "Queued",
    statusCompositing: "Processing",
    statusCapturing: "Capturing",
    statusFailed: "Failed",
    refresh: "Refresh",
    none: "No recordings found.",
    colStatus: "Status",
    colCall: "Call",
    colCase: "System / case / driver",
    colDuration: "Duration",
    colSize: "Size",
    colCreated: "Created",
    colCompleted: "Completed",
    play: "Play",
    download: "Download",
    manifest: "Manifest",
    signature: "Signature",
    details: "Recording details",
    notPlayable: "This recording is not available for playback yet (not completed or missing file).",
    seconds: "sec",
    close: "Close",
    loading: "Loading recording details...",
    selectHint: "Select a recording from the list to play or download it.",
    authTitle: "Recording playback sign-in",
    authHint: "A separate username and password are required only to play recordings.",
    username: "Username",
    password: "Password",
    authSubmit: "Sign in",
    authChecking: "Checking...",
    authFailed: "The username or password is incorrect.",
    authExpired: "Recording playback permission expired. Please sign in again."
  },
  errors: {
    "VC-AUTH-001": "The session expired. Please sign in again.",
    "VC-AUTH-002": "You are not authorized to perform this action.",
    "VC-LAUNCH-001": "Launch details are missing or invalid. Please contact the hosting system.",
    "VC-CALL-001": "The officer is not available right now.",
    "VC-CALL-002": "No answer. Try again or choose another officer.",
    "VC-CALL-003": "The officer declined the call.",
    "VC-CALL-004": "The system is at full capacity. Please try again in a few minutes.",
    "VC-MEDIA-001": "Camera and microphone access is required.",
    "VC-MEDIA-002": "Media error. Reconnecting...",
    "VC-MEDIA-003": "The connection failed. The call ended and the recording was saved.",
    "VC-MEDIA-004": "A secure HTTPS connection is required to access the camera and microphone. Open the demo with a secure URL.",
    "VC-REC-001": "Recording error. Please contact support.",
    "VC-CFG-001": "A configuration value is invalid.",
    "VC-SCHED-001": "Meeting details are invalid. Check duration and participants.",
    "VC-SCHED-002": "A completed meeting cannot be canceled.",
    "VC-SCHED-404": "Meeting not found.",
    "VC-RESYNC": "The connection resumed. Synchronizing call state...",
    "VC-SYS-001": "An error occurred. Please try again or contact support."
  }
} as const;
